import React, { useState, useRef, useMemo, useCallback } from "react";
import Draggable from "react-draggable";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  Spinner,
  FormGroup,
  Input,
  Label,
} from "reactstrap";
import html2canvas from "html2canvas";
import Select from "react-select";

// --- ICONS ---
import {
  Briefcase,
  Users,
  Award,
  MapPin,
  Phone,
  Globe,
  Mail,
} from "react-feather";

// --- HELPER COMPONENT FOR INJECTING CSS ---
const ModalStyles = () => {
  return (
    <style>{`
    /* General Layout & Responsiveness */
    .poster-modal .modal-dialog { max-width: 95vw !important; margin: 1.75rem auto; }
    @media (min-width: 1200px) { .poster-modal .modal-dialog { max-width: 1200px !important; } }
    .poster-modal-body { display: flex; flex-direction: column; padding: 1rem; gap: 1rem; }
    @media (min-width: 992px) { .poster-modal-body { flex-direction: row; gap: 2rem; } }
    .poster-preview-wrapper { flex: 3; display: flex; justify-content: center; align-items: center; }
    .poster-controls-wrapper { flex: 2; max-height: 75vh; overflow-y: auto; padding-right: 10px; }

    /* Poster Preview */
    .poster-preview { 
      position: relative; width: 100%; max-width: 480px; aspect-ratio: 8.5 / 11;
      background-size: cover; background-position: center; border-radius: 12px; 
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15); color: #000; overflow: hidden; 
      transition: all 0.3s ease; border: 1px solid #dee2e6; margin: 0 auto;
    }
    
    /* Draggable Elements */
    .draggable-element { 
      position: absolute; cursor: grab; padding: 5px; white-space: pre-wrap; line-height: 1.3;
      user-select: none; border: 1px dashed transparent; transition: all 0.2s ease;
    }
    .draggable-element:hover { border-color: rgba(0, 123, 255, 0.5); }
    .draggable-element:active { cursor: grabbing; z-index: 1000; transform: scale(1.02); }
    .draggable-logo { object-fit: contain; }

    /* Resizer handles */
    .resize-handle {
      position: absolute; width: 14px; height: 14px; background: #007bff; border: 2px solid white;
      border-radius: 50%; cursor: se-resize; opacity: 0; transition: opacity 0.2s ease; z-index: 1001;
      bottom: -7px; right: -7px; box-shadow: 0 0 5px rgba(0,0,0,0.3);
    }
    .draggable-element:hover .resize-handle, .resizing .resize-handle { opacity: 1; }
    
    /* CSS for hiding elements during capture */
    .poster-preview.is-capturing .resize-handle { display: none; }
    .poster-preview.is-capturing .draggable-element { border-color: transparent !important; }

    /* Template-specific data box layouts */
    .data-box-wrapper { width: 85%; padding: clamp(10px, 3vw, 1rem); }
    .data-box-corporate { background: rgba(240, 248, 255, 0.9); border-left: 4px solid #0066cc; backdrop-filter: blur(8px); }
    .data-box-modern { background: rgba(255, 255, 255, 0.9); border-radius: 16px; box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1); backdrop-filter: blur(12px); }
    .data-box-geometric { background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(240, 255, 240, 0.9) 100%); clip-path: polygon(0 0, calc(100% - 20px) 0, 100% 20px, 100% 100%, 20px 100%, 0 calc(100% - 20px)); }
    .data-box-abstract { background: rgba(255, 255, 255, 0.85); border-radius: 20px 4px 20px 4px; backdrop-filter: blur(15px); border: 1px solid rgba(255, 255, 255, 0.3); }
    .data-box-minimalist { background: rgba(248, 249, 250, 0.95); border-radius: 2px; border-top: 3px solid #333; box-shadow: 0 2px 20px rgba(0, 0, 0, 0.08); }

    .poster-data-item { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; word-break: break-word; font-size: clamp(10px, 1.5vw, 12px); line-height: 1.4; }
    .poster-data-item svg { flex-shrink: 0; color: var(--bs-primary, #007bff); width: 16px; height: 16px; }
    .poster-data-item strong { min-width: fit-content; font-weight: 600; }
    
    /* Controls styling */
    .control-group { border: 1px solid #e9ecef; border-radius: 12px; padding: 16px; margin-bottom: 16px; background: #fff; }
    .color-picker-wrapper { display: inline-block; border: 1px solid #ced4da; border-radius: 6px; overflow: hidden; }
    .color-picker-input { width: 45px; height: 36px; border: none; padding: 2px; background-color: transparent; cursor: pointer; }
    .template-select__option img, .template-select__control img { width: 40px; height: 24px; margin-right: 10px; border-radius: 4px; object-fit: cover; border: 1px solid #ddd; }
  `}</style>
  );
};

// --- STATIC DATA AND DEFAULTS ---
const TEMPLATES = [
  { id: 1, text: "We Are Hiring!", title: "Corporate Lines", image: "/img/template/t2.png", dataBoxClass: "data-box-corporate" },
  { id: 2, text: "Join Our Team", title: "Modern Blue", image: "/img/template/t1.png", dataBoxClass: "data-box-modern" },
  { id: 3, text: "Your Dream Job Awaits", title: "Geometric Green", image: "/img/template/t3.png", dataBoxClass: "data-box-geometric" },
  { id: 4, text: "Work With Us", title: "Abstract Waves", image: "/img/template/t4.png", dataBoxClass: "data-box-abstract" },
  { id: 5, text: "Exciting Career Opportunities", title: "Minimalist Sharp", image: "/img/template/t5.png", dataBoxClass: "data-box-minimalist" },
  { id: 6, text: "Exciting Career Opportunities", title: "Minimalist Sharp", image: "/img/template/t6.png", dataBoxClass: "data-box-minimalist" },
  { id: 7, text: "Exciting Career Opportunities", title: "Minimalist Sharp", image: "/img/template/t7.png", dataBoxClass: "data-box-minimalist" },
  { id: 8, text: "Exciting Career Opportunities", title: "Minimalist Sharp", image: "/img/template/t8.png", dataBoxClass: "data-box-minimalist" },
  { id: 9, text: "Exciting Career Opportunities", title: "Minimalist Sharp", image: "/img/template/t9.png", dataBoxClass: "data-box-geometric" },
  { id: 10, text: "Exciting Career Opportunities", title: "Minimalist Sharp", image: "/img/template/t10.png", dataBoxClass: "data-box-geometric" },
  { id: 11, text: "Exciting Career Opportunities", title: "Minimalist Sharp", image: "/img/template/t11.png", dataBoxClass: "data-box-geometric" },
  { id: 12, text: "Exciting Career Opportunities", title: "Minimalist Sharp", image: "/img/template/t12.png", dataBoxClass: "data-box-geometric" },
  { id: 13, text: "Exciting Career Opportunities", title: "Minimalist Sharp", image: "/img/template/t13.png", dataBoxClass: "data-box-geometric" },
  { id: 14, text: "Exciting Career Opportunities", title: "Minimalist Sharp", image: "/img/template/t14.png", dataBoxClass: "data-box-geometric" },
  { id: 15, text: "Exciting Career Opportunities", title: "Minimalist Sharp", image: "/img/template/t15.png", dataBoxClass: "data-box-geometric" },
  { id: 16, text: "Exciting Career Opportunities", title: "Minimalist Sharp", image: "/img/template/t16.png", dataBoxClass: "data-box-geometric" },
];

const DEFAULT_TOP_LOGO_STATE = { src: "/img/template/template_logo.png", position: { x: 20, y: 20 }, size: 100 };
const DEFAULT_BOTTOM_LOGO_STATE = { src: "/img/template/template_logo.png", position: { x: 35, y: 500 }, size: 80 };
const DEFAULT_HEADING_STATE = { text: "We're Hiring!", fontSize: 32, color: "#2c3e50", align: "center", position: { x: 40, y: 150 } };
const DEFAULT_DATA_BOX_STATE = { position: { x: 35, y: 240 } };

const EXPORT_WIDTH = 1080;
const EXPORT_HEIGHT = 1350;

// --- HELPER FUNCTION to convert any image src to a data URL
const imageToDataUrl = (src) =>
  new Promise((resolve, reject) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext("2d").drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = (err) => {
      console.error("Failed to convert image to data URL:", src, err);
      reject(err);
    };
    img.src = src;
  });


// --- STABLE CONTROL COMPONENT ---
const TextElementControls = ({ group, state, handleCustomizationChange, title }) => (
  <div className="control-group">
    <FormGroup>
      <Input
        name="text"
        type="textarea"
        rows="2"
        style={{ padding: "10px" }}
        value={state.text}
        onChange={(e) => handleCustomizationChange(group, e.target.name, e.target.value)}
        placeholder={`Enter ${title.toLowerCase()}...`}
      />
    </FormGroup>
    <Row>
      <Col>
        <FormGroup>
          <Label>Font Size ({state.fontSize}px)</Label>
          <Input
            name="fontSize"
            type="range"
            min="12"
            max="100"
            value={state.fontSize}
            onChange={(e) => handleCustomizationChange(group, e.target.name, e.target.value)}
          />
        </FormGroup>
      </Col>
      <Col xs="auto" className="d-flex align-items-center gap-2">
        <Label className="mb-0">Color</Label>
        <div className="color-picker-wrapper">
          <Input
            name="color"
            type="color"
            className="color-picker-input"
            value={state.color}
            onChange={(e) => handleCustomizationChange(group, e.target.name, e.target.value)}
          />
        </div>
      </Col>
    </Row>
  </div>
);

// --- MAIN COMPONENT ---
function TemplatesModal({ isOpen, toggle, data = {} }) {
  const [selectedId, setSelectedId] = useState(1);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isResizing, setIsResizing] = useState(false);

  const [topLogo, setTopLogo] = useState(DEFAULT_TOP_LOGO_STATE);
  const [bottomLogo, setBottomLogo] = useState(DEFAULT_BOTTOM_LOGO_STATE);
  const [heading, setHeading] = useState(DEFAULT_HEADING_STATE);
  const [dataBox, setDataBox] = useState(DEFAULT_DATA_BOX_STATE);

  // ✅ State for toggling the data box background
  const [bgcolor, setBgcolor] = useState(true);

  const previewRef = useRef(null);

  const selectedTemplate = useMemo(() => TEMPLATES.find((t) => t.id === selectedId), [selectedId]);
  const elementSetters = useMemo(() => ({ topLogo: setTopLogo, bottomLogo: setBottomLogo, heading: setHeading, dataBox: setDataBox }), []);
  const templateOptions = useMemo(() => TEMPLATES.map((t) => ({ value: t.id, label: t.title, image: t.image })), []);
  const currentTemplateOption = useMemo(() => templateOptions.find((opt) => opt.value === selectedId), [selectedId, templateOptions]);

  const handleResizeStart = useCallback((e, type, currentState) => {
    e.preventDefault();
    e.stopPropagation();
    const resizeStartRef = { startX: e.clientX, startY: e.clientY, startSize: currentState.size || currentState.fontSize };
    setIsResizing(true);
    const handleMouseMove = (moveEvent) => {
      const delta = moveEvent.clientX - resizeStartRef.startX + (moveEvent.clientY - resizeStartRef.startY);
      const newSize = Math.round(resizeStartRef.startSize + delta * 0.5);
      if (type === "heading") {
        setHeading((prev) => ({ ...prev, fontSize: Math.max(12, Math.min(100, newSize)) }));
      } else {
        elementSetters[type]?.((prev) => ({ ...prev, size: Math.max(30, Math.min(400, newSize)) }));
      }
    };
    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  }, [elementSetters]);

  const handleTemplateChange = useCallback((selectedOption) => {
    setSelectedId(selectedOption.value);
    setHeading((prev) => ({ ...prev, text: TEMPLATES.find((t) => t.id === selectedOption.value)?.text || "" }));
  }, []);

  const handleDragStop = useCallback((type, dragData) => {
    elementSetters[type]?.((prev) => ({ ...prev, position: { x: dragData.x, y: dragData.y } }));
  }, [elementSetters]);

  const handleCustomizationChange = useCallback((group, name, value) => {
    elementSetters[group]?.((prev) => ({ ...prev, [name]: ["fontSize", "size"].includes(name) ? Number(value) : value }));
  }, [elementSetters]);

  // --- FINAL, ROBUST DOWNLOAD HANDLER ---
  const handleDownload = async () => {
    const contentElement = previewRef.current;
    if (!contentElement) return;

    setIsDownloading(true);

    // --- Store original state for cleanup ---
    const originalBg = contentElement.style.backgroundImage;
    const topLogoEl = contentElement.querySelector('img[alt="Top Logo"]');
    const bottomLogoEl = contentElement.querySelector('img[alt="Bottom Logo"]');
    const originalTopSrc = topLogoEl ? topLogoEl.src : null;
    const originalBottomSrc = bottomLogoEl ? bottomLogoEl.src : null;

    contentElement.classList.add("is-capturing");

    try {
      // 1. Pre-load all images into data URLs to eliminate timing issues
      const [topLogoDataUrl, bottomLogoDataUrl] = await Promise.all([
        imageToDataUrl(originalTopSrc),
        imageToDataUrl(originalBottomSrc),
      ]);

      // Temporarily apply data URLs to the live elements for capture
      if (topLogoEl && topLogoDataUrl) topLogoEl.src = topLogoDataUrl;
      if (bottomLogoEl && bottomLogoDataUrl) bottomLogoEl.src = bottomLogoDataUrl;

      // 2. Create the final canvas and render the background correctly
      const finalCanvas = document.createElement('canvas');
      finalCanvas.width = EXPORT_WIDTH;
      finalCanvas.height = EXPORT_HEIGHT;
      const ctx = finalCanvas.getContext('2d');

      const bgImage = new Image();
      bgImage.crossOrigin = "anonymous";
      bgImage.src = selectedTemplate.image;
      await new Promise((resolve, reject) => {
        bgImage.onload = resolve;
        bgImage.onerror = reject;
      });

      const canvasAspect = EXPORT_WIDTH / EXPORT_HEIGHT;
      const bgAspect = bgImage.naturalWidth / bgImage.naturalHeight;
      let sx, sy, sWidth, sHeight;

      if (bgAspect > canvasAspect) {
        sHeight = bgImage.naturalHeight;
        sWidth = bgImage.naturalHeight * canvasAspect;
        sx = (bgImage.naturalWidth - sWidth) / 2;
        sy = 0;
      } else {
        sWidth = bgImage.naturalWidth;
        sHeight = bgImage.naturalWidth / canvasAspect;
        sx = 0;
        sy = (bgImage.naturalHeight - sHeight) / 2;
      }
      ctx.drawImage(bgImage, sx, sy, sWidth, sHeight, 0, 0, EXPORT_WIDTH, EXPORT_HEIGHT);

      // 3. Capture only the content (now with pre-loaded logos)
      contentElement.style.backgroundImage = 'none'; // Hide CSS background for capture
      const contentCanvas = await html2canvas(contentElement, {
        backgroundColor: null,
        scale: EXPORT_WIDTH / contentElement.offsetWidth,
        useCORS: true,
      });

      // 4. Draw the captured content over the background
      ctx.drawImage(contentCanvas, 0, 0, EXPORT_WIDTH, EXPORT_HEIGHT);

      // 5. Trigger download
      const link = document.createElement('a');
      link.download = `Job-Poster-${data?.fld_title?.replace(/[^a-zA-Z0-9]/g, "-") || "New"}.jpg`;
      link.href = finalCanvas.toDataURL('image/jpeg', 0.95);
      link.click();

    } catch (error) {
      console.error("Poster creation failed:", error);
      alert("An error occurred while creating the poster. Please check the console and try again.");
    } finally {
      // 6. ALWAYS clean up and restore the original state
      contentElement.style.backgroundImage = originalBg;
      if (topLogoEl && originalTopSrc) topLogoEl.src = originalTopSrc;
      if (bottomLogoEl && originalBottomSrc) bottomLogoEl.src = originalBottomSrc;
      contentElement.classList.remove("is-capturing");
      setIsDownloading(false);
    }
  };

  const formatOptionLabel = ({ label, image }) => (
    <div className="d-flex align-items-center">
      <img src={image} alt={label} /> <span>{label}</span>
    </div>
  );

  const JOB_DATA_ITEMS = useMemo(() => [
    { icon: Briefcase, label: "Job Title", value: data?.fld_title || "Software Developer" },
    { icon: Users, label: "Positions", value: data?.fld_positions || "5" },
    { icon: Award, label: "Qualification", value: data?.fld_qualification || "Bachelor's Degree" },
    { icon: MapPin, label: "Location", value: data?.fld_city || "Mumbai" },
    { type: "divider" },
    { icon: Phone, label: "Contact", value: "+91-9319179297" },
    { icon: Globe, label: "Website", value: "www.beteljobs.com" },
    { icon: Mail, label: "Email", value: "support@beteljobs.com" },
  ], [data]);

  return (
    <>
      <ModalStyles />
      <Modal isOpen={isOpen} toggle={toggle} className="poster-modal" centered size="lg" backdrop="static">
        <ModalHeader toggle={toggle}>Create & Download Job Poster</ModalHeader>
        <ModalBody className="poster-modal-body">
          <div className="poster-preview-wrapper">
            <div ref={previewRef} className="poster-preview" style={{ backgroundImage: `url(${selectedTemplate?.image})` }}>
              <Draggable bounds="parent" onStop={(e, d) => handleDragStop("heading", d)} position={heading.position} disabled={isResizing}>
                <div className={`draggable-element ${isResizing ? "resizing" : ""}`} style={{ fontSize: `${heading.fontSize}px`, color: heading.color, textAlign: heading.align, fontWeight: "bold", width: "80%" }}>
                  {heading.text}
                  <div className="resize-handle" onMouseDown={(e) => handleResizeStart(e, "heading", heading)} />
                </div>
              </Draggable>
              {topLogo.src && (
                <Draggable bounds="parent" onStop={(e, d) => handleDragStop("topLogo", d)} position={topLogo.position} disabled={isResizing}>
                  <div className={`draggable-element ${isResizing ? "resizing" : ""}`} style={{ width: `${topLogo.size}px` }}>
                    <img src={topLogo.src} alt="Top Logo" className="draggable-logo" style={{ width: "100%", height: "auto" }} />
                    <div className="resize-handle" onMouseDown={(e) => handleResizeStart(e, "topLogo", topLogo)} />
                  </div>
                </Draggable>
              )}
              {/* {bottomLogo.src && (
                <Draggable bounds="parent" onStop={(e, d) => handleDragStop("bottomLogo", d)} position={bottomLogo.position} disabled={isResizing}>
                  <div className={`draggable-element ${isResizing ? "resizing" : ""}`} style={{ width: `${bottomLogo.size}px` }}>
                    <img src={bottomLogo.src} alt="Bottom Logo" className="draggable-logo" style={{ width: "100%", height: "auto" }} />
                    <div className="resize-handle" onMouseDown={(e) => handleResizeStart(e, "bottomLogo", bottomLogo)} />
                  </div>
                </Draggable>
              )} */}
              <Draggable bounds="parent" onStop={(e, d) => handleDragStop("dataBox", d)} position={dataBox.position} disabled={isResizing}>
                {/* ✅ ClassName is now conditional based on 'bgcolor' state */}
                <div className={`draggable-element data-box-wrapper ${bgcolor ? selectedTemplate?.dataBoxClass : ""}`}>
                  {JOB_DATA_ITEMS.map((item, index) =>
                    item.type === "divider" ? (
                      <hr key={index} className="my-2" />
                    ) : (
                      <div key={index} className="poster-data-item">
                        <item.icon />
                        <div>
                          <strong>{item.label}:</strong> {item.value || "N/A"}
                        </div>
                      </div>
                    )
                  )}
                </div>
              </Draggable>
            </div>
          </div>
          <div className="poster-controls-wrapper">
            <Label className="mb-1">Select Template</Label>
            <div className="control-group">
              <Select
                value={currentTemplateOption}
                onChange={handleTemplateChange}
                options={templateOptions}
                formatOptionLabel={formatOptionLabel}
                classNamePrefix="template-select"
              />
            </div>

            <Label className="mb-1 mt-4">Poster Heading</Label>
            <TextElementControls
              group="heading"
              state={heading}
              handleCustomizationChange={handleCustomizationChange}
              title="Main Heading"
            />

            {/* ✅ Button to toggle the data box background */}
            <Button
              color="info"
              outline
              onClick={() => setBgcolor(!bgcolor)}
              className="mt-3  w-100"
              style={{ backgroundColor: "#6DB2C5" }}
            >
              Data Box Background: <strong>{bgcolor ? "ON" : "OFF"}</strong>
            </Button>
          </div>

        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggle}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleDownload}
            disabled={isDownloading}
            style={{ minWidth: "170px" }}
          >
            {isDownloading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Generating...
              </>
            ) : (
              "Download Poster"
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default TemplatesModal;